import React, { useEffect, useState } from "react";
import Header from "../../components/ui/Header";
import Sidebar from "../../components/ui/Sidebar";
import Button from "../../components/ui/Button";
import Icon from "../../components/AppIcon";
import { useNavigate } from "react-router-dom";
import CreateOrderModal from "./components/CreateOrderModal";
import axiosInstance from "../../utils/axiosInstance";
import { useAuth } from "../../context/AuthContext";
import { canAccess } from "../../utils/permissions";

// utils
import {
  formatDateForAPI,
  formatCurrency,
  formatDate,
  formatSlotRange,
  formatCenterSlotRange,
  getStatusBadge,
  getPaymentStatusBadge,
} from "../../utils/orderFormatters";

// components
import VenueDisplay from "./components/VenueDisplay";
import B2BDisplay from "./components/B2BDisplay";
import ReportIndicator from "./components/ReportIndicator";

const OrdersManagement = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({
    currentPage: 1,
    totalPages: 1,
    total: 0,
    perPage: 10,
  });

  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [paymentStatusFilter, setPaymentStatusFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");

  // NEW filters
  const [refCenterFilter, setRefCenterFilter] = useState("all"); // refCenterId
  const [venueFilter, setVenueFilter] = useState("all"); // diagnosticCenterId
  const [b2bCenterFilter, setB2bCenterFilter] = useState("all"); // centerId

  // Modals
  const [showCreateOrderModal, setShowCreateOrderModal] = useState(false);

  // Dropdown data
  const [refCenters, setRefCenters] = useState([]);
  const [isLoadingRefCenters, setIsLoadingRefCenters] = useState(false);

  const [venueData, setVenueData] = useState([]);
  const [b2bData, setB2bData] = useState([]);
  const [sourceData, setsourceData] = useState([]);

  const [isLoadingVenue, setIsLoadingVenue] = useState(false);
  const [isLoadingB2b, setIsLoadingB2b] = useState(false);

  const API_BASE = import.meta.env.VITE_API_BASE_URL;

  const handleMenuToggle = () => setSidebarOpen((s) => !s);
  const handleSidebarClose = () => setSidebarOpen(false);
  const handleViewDetails = (orderId) => navigate(`/orders/${orderId}`);

  const isFilterActive = () => {
    return (
      searchTerm !== "" ||
      statusFilter !== "all" ||
      paymentStatusFilter !== "all" ||
      sourceFilter !== "all" ||
      refCenterFilter !== "all" ||
      venueFilter !== "all" ||
      b2bCenterFilter !== "all"
    );
  };

  const resetFilters = () => {
    setSearchTerm("");
    setStatusFilter("all");
    setPaymentStatusFilter("all");
    setSourceFilter("all");
    setRefCenterFilter("all");
    setVenueFilter("all");
    setB2bCenterFilter("all");
    setPage(1);

    setTimeout(() => fetchOrders({ page: 1 }), 0);
  };

  // Fetch Ref Centers
  const fetchRefCenters = async () => {
    try {
      setIsLoadingRefCenters(true);
      const res = await axiosInstance.get(`${API_BASE}/api/reference-centers`, {
        withCredentials: true,
      });
      setRefCenters(res.data?.data || []);
    } catch (err) {
      console.error("Error fetching ref centers:", err);
    } finally {
      setIsLoadingRefCenters(false);
    }
  };

  // Fetch Venue + B2B dropdown lists
  const fetchVenueAndB2b = async () => {
    try {
      setIsLoadingVenue(true);
      setIsLoadingB2b(true);

      const [venueRes, b2bRes, source] = await Promise.all([
        axiosInstance.get(`${API_BASE}/api/diagnostic-centers`, {
          withCredentials: true,
        }),
        axiosInstance.get(`${API_BASE}/api/centers`, { withCredentials: true }),
        axiosInstance.get(`${API_BASE}/api/source`, { withCredentials: true }),
      ]);

      setVenueData(venueRes.data?.data || []);
      setB2bData(b2bRes.data?.data || []);
      setsourceData(source.data?.data || []);
    } catch (err) {
      console.error("Error fetching venue/b2b:", err);
    } finally {
      setIsLoadingVenue(false);
      setIsLoadingB2b(false);
    }
  };

  // ✅ Fetch Orders (TODAY ONLY)
  const fetchOrders = async (override = {}) => {
    try {
      setIsLoading(true);
      setError(null);

      const today = new Date();
      const fromDate = formatDateForAPI(today);
      const toDate = formatDateForAPI(today);

      const final = {
        page,
        limit: 10,
        search: searchTerm,
        status: statusFilter === "all" ? "" : statusFilter,
        paymentStatus: paymentStatusFilter === "all" ? "" : paymentStatusFilter,

        source: sourceFilter === "all" ? "" : sourceFilter,
        // Workspace hides mobile-app bookings by default — those live on
        // the dedicated Online Booking screen. If the user explicitly picks
        // "App" in the Source dropdown, stop excluding so they can see them.
        excludeSource: sourceFilter === "app" ? "" : "app",
        refCenterId: refCenterFilter === "all" ? "" : refCenterFilter,
        diagnosticCenterId: venueFilter === "all" ? "" : venueFilter,
        centerId: b2bCenterFilter === "all" ? "" : b2bCenterFilter,

        ...override,
      };

      const res = await axiosInstance.get(`${API_BASE}/api/orders`, {
        withCredentials: true,
        params: {
          ...final,
          fromDate,
          toDate,
        },
      });

      setOrders(res.data?.orders || []);
      setMeta(
        res.data?.meta || {
          currentPage: 1,
          totalPages: 1,
          total: 0,
          perPage: 10,
        },
      );
    } catch (err) {
      console.error("Error fetching orders:", err);
      setError(
        err.response?.data?.message ||
          "Failed to load orders. Please try again.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchOrders({ page: 1 });
  };

  // initial loads
  useEffect(() => {
    fetchRefCenters();
    fetchVenueAndB2b();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // debounce fetch on filter changes (TODAY ONLY)
  useEffect(() => {
    const t = setTimeout(() => fetchOrders(), 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    searchTerm,
    statusFilter,
    paymentStatusFilter,
    sourceFilter,
    refCenterFilter,
    venueFilter,
    b2bCenterFilter,
    page,
  ]);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header onMenuToggle={handleMenuToggle} isMenuOpen={sidebarOpen} />
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        isCollapsed={sidebarCollapsed}
        onCollapseToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
      />

      <main
        className={`pt-16 transition-all duration-300 ${
          sidebarCollapsed ? "lg:ml-16" : "lg:ml-60"
        }`}>
        <div className="p-4 lg:p-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Icon
                    name="ShoppingBag"
                    size={18}
                    className="text-blue-600"
                  />
                </div>
                <h1 className="text-xl lg:text-2xl font-bold text-gray-900">
                  Lab Orders (Today)
                </h1>
              </div>
              <p className="text-sm text-gray-600 mt-1 ml-10">
                Manage laboratory test orders, track status, and view reports
              </p>
            </div>

            <div className="flex items-center gap-2">
              {/* Expiring Orders Button */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/expire-orders")}
                className="text-xs px-3 py-2 border-amber-200 text-amber-600 hover:bg-amber-50 hover:text-amber-700">
                <Icon name="Clock" size={14} className="mr-1.5" />
                Expiring Orders
              </Button>

              {/* Create New Order Button */}
              <Button
                onClick={() => navigate("/orders/new")}
                disabled={!canAccess(currentUser, "canCreate")}
                className="bg-blue-600 hover:bg-blue-700 text-white text-xs px-3 py-2 shadow-sm hover:shadow disabled:opacity-50 disabled:cursor-not-allowed">
                <Icon name="Plus" size={14} className="mr-1.5" />
                Create New Order
              </Button>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm mb-6">
            <form onSubmit={handleSearch} className="space-y-4">
              {/* ✅ Row 1 */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-4">
                {/* Search */}
                <div className="lg:col-span-4">
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Search Orders
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Icon name="Search" size={14} className="text-gray-400" />
                    </div>
                    <input
                      type="text"
                      placeholder="Order ID, patient name, tracking ID..."
                      value={searchTerm}
                      onChange={(e) => {
                        setPage(1);
                        setSearchTerm(e.target.value);
                      }}
                      className="block w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg bg-white placeholder-gray-400 text-gray-900
                       focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-xs"
                    />
                  </div>
                </div>

                {/* Venue */}
                <div className="lg:col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Venue
                  </label>
                  <select
                    value={venueFilter}
                    onChange={(e) => {
                      setPage(1);
                      setVenueFilter(e.target.value);
                    }}
                    disabled={isLoadingVenue}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900
                     focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-xs">
                    <option value="all">All Venues</option>
                    {venueData.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* B2B Center */}
                <div className="lg:col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    B2B Center
                  </label>
                  <select
                    value={b2bCenterFilter}
                    onChange={(e) => {
                      setPage(1);
                      setB2bCenterFilter(e.target.value);
                    }}
                    disabled={isLoadingB2b}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900
                     focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-xs">
                    <option value="all">All B2B Centers</option>
                    {b2bData.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Source — dynamic list from /api/source, with a static
                    "Online" entry (value=app) pinned at the end. */}
                <div className="lg:col-span-1">
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Source
                  </label>
                  <select
                    value={sourceFilter}
                    onChange={(e) => {
                      setPage(1);
                      setSourceFilter(e.target.value);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900
                     focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-xs">
                    <option value="all">All</option>
                    {sourceData.map((b) => (
                      <option key={b.id} value={b.name}>
                        {b.name}
                      </option>
                    ))}
                    <option value="app">Online</option>
                  </select>
                </div>

                {/* Status */}
                <div className="lg:col-span-1">
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Status
                  </label>
                  <select
                    value={statusFilter}
                    onChange={(e) => {
                      setPage(1);
                      setStatusFilter(e.target.value);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900
                     focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-xs">
                    <option value="all">All</option>
                    <option value="pending">Pending</option>
                    <option value="confirmed">Confirmed</option>
                    <option value="sample_collected">Sample Collected</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>

                {/* Payment */}
                <div className="lg:col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Payment
                  </label>
                  <select
                    value={paymentStatusFilter}
                    onChange={(e) => {
                      setPage(1);
                      setPaymentStatusFilter(e.target.value);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900
                     focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-xs">
                    <option value="all">All</option>
                    <option value="pending">Due</option>
                    <option value="paid">Paid</option>
                  </select>
                </div>
              </div>

              {/* ✅ Row 2 */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-4 items-end">
                {/* Ref Center */}
                <div className="lg:col-span-4">
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Ref-Center
                  </label>
                  <select
                    value={refCenterFilter}
                    onChange={(e) => {
                      setPage(1);
                      setRefCenterFilter(e.target.value);
                    }}
                    disabled={isLoadingRefCenters}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-900
                     focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-xs">
                    <option value="all">All Ref Centers</option>
                    {refCenters.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Reset button aligned to right */}
                <div className="lg:col-span-8 flex justify-start md:justify-end">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={resetFilters}
                    disabled={!isFilterActive()}
                    className="text-xs px-4 py-2 text-gray-600 hover:text-gray-900
                     disabled:opacity-50 disabled:cursor-not-allowed">
                    <Icon name="RotateCcw" size={12} className="mr-1.5" />
                    Reset All
                  </Button>
                </div>
              </div>
            </form>
          </div>

          {/* Table */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-5 py-4 bg-gradient-to-r from-gray-50 to-white border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Icon name="Package" size={16} className="text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">
                      Today’s Order Records
                    </h3>
                    <p className="text-xs text-gray-600">
                      {meta.total || 0} orders found
                    </p>
                  </div>
                </div>

                {meta.totalPages > 1 && (
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-lg border border-gray-200">
                    <span className="text-xs font-medium text-gray-700">
                      Page {meta.currentPage} of {meta.totalPages}
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-16">
                      #
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Order Details
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Patient
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Reference
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Venue
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      B2B Center
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Schedule
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Amount
                    </th>

                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>

                <tbody className="bg-white divide-y divide-gray-200">
                  {isLoading ? (
                    <tr>
                      <td colSpan="10" className="px-4 py-8 text-center">
                        <div className="flex flex-col items-center justify-center">
                          <div className="w-8 h-8 border-2 border-gray-200 border-t-blue-600 rounded-full animate-spin"></div>
                          <p className="mt-2 text-xs text-gray-500">
                            Loading orders...
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : error ? (
                    <tr>
                      <td colSpan="10" className="px-4 py-8 text-center">
                        <div className="flex flex-col items-center justify-center">
                          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-2">
                            <Icon
                              name="AlertCircle"
                              size={24}
                              className="text-red-500"
                            />
                          </div>
                          <p className="text-red-600 font-medium text-sm">
                            Error Loading Orders
                          </p>
                          <p className="text-red-500 text-xs mt-1">{error}</p>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => fetchOrders()}
                            className="mt-3 text-xs px-4 py-2">
                            Try Again
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ) : orders.length === 0 ? (
                    <tr>
                      <td colSpan="10" className="px-4 py-8 text-center">
                        <div className="flex flex-col items-center justify-center">
                          <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-2">
                            <Icon
                              name="ShoppingBag"
                              size={24}
                              className="text-gray-400"
                            />
                          </div>
                          <p className="text-gray-600 font-medium text-sm">
                            No orders found for today
                          </p>
                          <p className="text-gray-400 text-xs mt-1">
                            Try adjusting your filters
                          </p>
                          {isFilterActive() && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={resetFilters}
                              className="mt-3 text-xs px-4 py-2">
                              Clear Filters
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ) : (
                    orders.map((order, index) => (
                      <tr
                        key={order.id}
                        onClick={() => handleViewDetails(order.id)}
                        className="cursor-pointer hover:bg-gray-50/80 transition-colors duration-150 group">
                        <td className="px-4 py-3 text-black-500 font-mono text-xs">
                          #
                          {String(
                            meta.total -
                              ((meta.currentPage - 1) * meta.perPage + index),
                          ).padStart(2, "0")}
                        </td>

                        {/* Order Details */}
                        <td className="px-4 py-3">
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-semibold text-gray-900">
                                ORD-{order.id}
                              </span>
                            </div>
                            <div className="flex items-center gap-1">
                              <div
                                className={`w-1.5 h-1.5 rounded-full ${
                                  order.isHomeSample
                                    ? "bg-emerald-500"
                                    : "bg-blue-500"
                                }`}
                              />
                              <span
                                className={`text-[10px] font-medium ${
                                  order.isHomeSample
                                    ? "text-emerald-600"
                                    : "text-blue-600"
                                }`}>
                                {order.isHomeSample
                                  ? "Home Collection"
                                  : "Center Visit"}
                              </span>
                            </div>
                            <div className="text-[10px] text-gray-600">
                              {new Date(order.createdAt).toLocaleDateString(
                                "en-IN",
                                {
                                  day: "numeric",
                                  month: "short",
                                  year: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                },
                              )}
                            </div>
                          </div>
                        </td>

                        {/* Patient */}
                        <td className="px-4 py-3">
                          <div className="space-y-1">
                            {(order.orderMembers?.length > 0
                              ? order.orderMembers.map((m) => m.patient?.fullName).filter(Boolean)
                              : [order.patient?.fullName].filter(Boolean)
                            ).map((name, i) => (
                              <div key={i} className="text-xs font-medium text-gray-900">{name}</div>
                            ))}
                            <div className="flex items-center">
                              <span className="text-[10px] text-gray-600">
                                {order.patient?.contactNo || "No contact"}
                              </span>
                            </div>
                            <span className="text-[9px] px-1.5 py-0.5 bg-gray-100 rounded text-gray-600">
                              {order.source || "app"}
                            </span>
                          </div>
                        </td>

                        {/* Reference (Doctor & Ref Center) */}
                        <td className="px-4 py-3 min-w-[120px]">
                          <div className="space-y-2">
                            {/* Doctor */}
                            <div className="flex items-center gap-1.5 text-[10px] text-gray-700">
                              <Icon name="Stethoscope" size={12} className="text-blue-500 shrink-0" />
                              <span className="truncate" title={order.doctor?.name ? `Dr. ${order.doctor.name}` : "Self"}>
                                {order.doctor?.name ? `Dr. ${order.doctor.name}` : "Self"}
                              </span>
                            </div>
                            {/* Ref Center */}
                            {order.refCenter && (
                              <div className="flex items-center gap-1.5 text-[10px] text-gray-600">
                                <Icon name="Building2" size={12} className="text-amber-500 shrink-0" />
                                <span className="truncate" title={order.refCenter.name}>
                                  {order.refCenter.name}
                                </span>
                              </div>
                            )}
                          </div>
                        </td>

                        {/* Venue */}
                        <td className="px-4 py-3 max-w-[240px]">
                          <VenueDisplay order={order} />
                        </td>

                        {/* B2B */}
                        <td className="px-4 py-3 max-w-[240px]">
                          <B2BDisplay order={order} />
                        </td>

                        {/* Schedule */}
                        <td className="px-4 py-3">
                          <div className="space-y-1">
                            <div className="flex items-center gap-1">
                              <Icon
                                name="Calendar"
                                size={10}
                                className="text-gray-400"
                              />
                              <span className="text-[10px] text-gray-700">
                                {formatDate(order.date)}
                              </span>
                            </div>
                            {order.slot && (
                              <div className="flex items-center gap-1">
                                <Icon
                                  name="Clock"
                                  size={10}
                                  className="text-gray-400"
                                />
                                <span className="text-[10px] text-gray-500">
                                  {formatSlotRange(order.slot)}
                                </span>
                              </div>
                            )}
                            {order.centerSlot && (
                              <div className="flex items-center gap-1">
                                <Icon
                                  name="Clock"
                                  size={10}
                                  className="text-gray-400"
                                />
                                <span className="text-[10px] text-gray-500">
                                  {formatCenterSlotRange(order.centerSlot)}
                                </span>
                              </div>
                            )}
                          </div>
                        </td>

                        {/* Amount */}
                        <td className="px-4 py-3">
                          <div className="space-y-2">
                            <div>
                              <span className="text-xs font-semibold text-gray-900">
                                {formatCurrency(order.finalAmount || 0)}
                              </span>
                              {order.discount > 0 && (
                                <div className="flex items-center gap-1 mt-0.5">
                                  <span className="text-[9px] line-through text-gray-400">
                                    {formatCurrency(order.totalAmount)}
                                  </span>
                                  <span className="text-[9px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded">
                                    -{formatCurrency(order.discount)}
                                  </span>
                                </div>
                              )}
                            </div>

                            <div>
                              <span
                                className={`inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-medium ${getPaymentStatusBadge(
                                  order.paymentStatus,
                                )}`}>
                                {order.paymentStatus === "pending"
                                  ? "DUE"
                                  : order.paymentStatus}
                              </span>
                              {order.paymentMode && (
                                <div className="text-[9px] text-gray-500 mt-0.5">
                                  via {order.paymentMode}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>

                        {/* Status */}
                        <td className="px-4 py-3">
                          <div className="space-y-1.5">
                            <span
                              className={`inline-flex items-center px-2 py-1 rounded-full text-[10px] font-medium ${getStatusBadge(
                                order.status,
                              )}`}>
                              <span
                                className={`w-1 h-1 rounded-full mr-1.5 ${
                                  order.status === "completed"
                                    ? "bg-emerald-500"
                                    : order.status === "cancelled"
                                      ? "bg-rose-500"
                                      : order.status === "pending"
                                        ? "bg-amber-500"
                                        : order.status === "sample_collected"
                                          ? "bg-violet-500"
                                          : order.status === "confirmed"
                                            ? "bg-sky-500"
                                            : "bg-gray-500"
                                }`}></span>
                              {order.status?.replaceAll("_", " ") || "Pending"}
                            </span>

                            <ReportIndicator
                              reportReady={order.reportReady}
                              status={order.status}
                            />
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {meta?.totalPages > 1 && (
              <div className="px-5 py-4 bg-gray-50/80 border-t border-gray-200">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="text-xs text-gray-700">
                    Showing{" "}
                    <span className="font-medium">
                      {(meta.currentPage - 1) * meta.perPage + 1}
                    </span>{" "}
                    -{" "}
                    <span className="font-medium">
                      {Math.min(meta.currentPage * meta.perPage, meta.total)}
                    </span>{" "}
                    of <span className="font-medium">{meta.total}</span> orders
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page === 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      className="flex items-center gap-1 text-xs px-3 py-1.5 border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed">
                      <Icon name="ChevronLeft" size={14} />
                      Previous
                    </Button>

                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page === meta.totalPages}
                      onClick={() => setPage((p) => p + 1)}
                      className="flex items-center gap-1 text-xs px-3 py-1.5 border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed">
                      Next
                      <Icon name="ChevronRight" size={14} />
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Modal */}
      {showCreateOrderModal && (
        <CreateOrderModal
          isOpen={showCreateOrderModal}
          onClose={() => setShowCreateOrderModal(false)}
          onOrderCreated={fetchOrders}
        />
      )}
    </div>
  );
};

export default OrdersManagement;
